import '../styles/main.css';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { gsap } from 'gsap';
import SceneManager from './scene/SceneManager';
import * as Tone from 'tone';
import SoundManager from './scene/SoundManager';

function setAppHeightVar() {
  const height = window.visualViewport?.height ?? window.innerHeight;
  document.documentElement.style.setProperty('--app-height', `${Math.round(height)}px`);

  // Keep Three.js renderer/camera in sync when mobile browser UI changes viewport height
  try {
    window.app?.sceneManager?.onWindowResize?.();
  } catch (_) {
    // noop
  }
}

// Initialize the main application
class Main {
  constructor() {
    // First check if required containers exist
    this.canvasContainer = document.getElementById('canvas-container');
    this.sceneContainer = document.getElementById('scene-container');
    
    if (!this.canvasContainer || !this.sceneContainer) {
      throw new Error('Required container elements not found! Make sure canvas-container and scene-container exist.');
    }

    // Initialize audio context and sound manager
    this.initializeAudio();
    
    // Mount Three.js renderer inside `#scene-container` so it stays interactive even when
    // other overlay elements (UI sections) are present.
    this.sceneManager = new SceneManager(this.sceneContainer);
    this.sections = ['home', 'work', 'media', 'contact'];
    this.currentSection = 'home';
    this.isScrolling = false;
    this.scrollCooldown = 1000; // ms
    // Require more intentional scrolling at section edges before jumping sections.
    // This prevents accidental section transitions while browsing long lists (projects/media).
    this.wheelNavigateThreshold = 360; // px of accumulated wheel delta at the edge
    this.wheelNavigateWindowMs = 550; // ms window to accumulate scroll intent
    this._wheelIntent = { accum: 0, dir: 0, lastTs: 0 };
    this.setupEventListeners();
    this.animate();
  }

  async initializeAudio() {
    const startAudio = async () => {
      try {
        await Tone.start();
        
        // Initialize sound manager after audio context is started
        if (!window.soundManager) {
          window.soundManager = new SoundManager();
        }
        
        // Remove listeners once audio is started
        document.removeEventListener('click', startAudio);
        document.removeEventListener('touchstart', startAudio);
        
        // Signal that audio is ready
        window.audioContextStarted = true;
        document.dispatchEvent(new Event('audioContextStarted'));
      } catch (error) {
        console.error('Failed to start audio context:', error);
      }
    };

    // Create sound manager immediately
    if (!window.soundManager) {
      window.soundManager = new SoundManager();
    }

    // Add event listeners for both click and touch
    document.addEventListener('click', startAudio);
    document.addEventListener('touchstart', startAudio);
  }

  setupEventListeners() {
    // Mobile navigation toggle
    const mobileToggle = document.querySelector('.mobile-nav-toggle');
    const navigation = document.querySelector('.navigation');
    
    if (mobileToggle && navigation) {
      mobileToggle.addEventListener('click', () => {
        const isOpen = navigation.classList.contains('open');
        navigation.classList.toggle('open');
        mobileToggle.classList.toggle('open');
        mobileToggle.setAttribute('aria-expanded', !isOpen);
        
        // Prevent body scroll when menu is open
        document.body.style.overflow = 'hidden';
      });
      
      // Close mobile menu when clicking outside
      document.addEventListener('click', (e) => {
        if (!navigation.contains(e.target) && !mobileToggle.contains(e.target)) {
          navigation.classList.remove('open');
          mobileToggle.classList.remove('open');
          mobileToggle.setAttribute('aria-expanded', 'false');
          document.body.style.overflow = 'hidden';
        }
      });
    }

    // Navigation menu click events
    document.querySelectorAll('.nav-item').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const section = e.target.getAttribute('data-section');
        this.navigateToSection(section);
        
        // Close mobile menu after navigation
        if (navigation && navigation.classList.contains('open')) {
          navigation.classList.remove('open');
          mobileToggle?.classList.remove('open');
          mobileToggle?.setAttribute('aria-expanded', 'false');
          document.body.style.overflow = 'hidden';
        }
      });
    });

    // Work items click events
    document.querySelectorAll('.work-item').forEach(item => {
      item.addEventListener('click', async (e) => {
        e.preventDefault();
        const project = {
          id: item.getAttribute('data-project'),
          title: item.querySelector('.project-name').textContent,
          image: `/src/assets/projects/${item.getAttribute('data-project')}.png`
        };
        
        // Show project details card on mobile
        const projectCard = document.querySelector('.project-details-card');
        if (projectCard && window.innerWidth <= 768) {
          projectCard.classList.add('mobile-sheet');
          // Clear any leftover inline transform (e.g. from drag-to-close)
          projectCard.style.transform = '';
          // Trigger animation reliably (ensure browser registers the initial transform state)
          projectCard.classList.remove('active');
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              projectCard.classList.add('active');
            });
          });
          // Prevent body scroll when card is open
          document.body.style.overflow = 'hidden';
          
          // Add close button for mobile
          let closeBtn = projectCard.querySelector('.mobile-close-btn');
          if (!closeBtn) {
            closeBtn = document.createElement('button');
            closeBtn.className = 'mobile-close-btn';
            closeBtn.innerHTML = '×';
            closeBtn.setAttribute('aria-label', 'Close project details');
            projectCard.querySelector('.card-content').prepend(closeBtn);
            
            const closeCard = () => {
              projectCard.classList.remove('active');
              projectCard.style.transform = '';
              document.body.style.overflow = '';
            };
            
            closeBtn.addEventListener('click', closeCard);
            
            // Add swipe-down gesture to close
            let touchStartY = 0;
            let touchStartTime = 0;
            let isScrolling = false;
            
            projectCard.addEventListener('touchstart', (e) => {
              touchStartY = e.touches[0].clientY;
              touchStartTime = Date.now();
              isScrolling = false;
            }, { passive: true });
            
            projectCard.addEventListener('touchmove', (e) => {
              const touchY = e.touches[0].clientY;
              const deltaY = touchY - touchStartY;
              
              // If scrolling down and at the top of the card content
              if (deltaY > 10 && projectCard.scrollTop === 0) {
                isScrolling = true;
                // Allow some downward drag
                if (deltaY > 30) {
                  projectCard.style.transform = `translateY(${deltaY}px)`;
                }
              }
            }, { passive: true });
            
            projectCard.addEventListener('touchend', (e) => {
              const touchEndY = e.changedTouches[0].clientY;
              const deltaY = touchEndY - touchStartY;
              const deltaTime = Date.now() - touchStartTime;
              
              // Reset transform
              projectCard.style.transform = '';
              
              // If swiped down significantly (more than 100px) or fast downward swipe
              if ((deltaY > 100) || (deltaY > 50 && deltaTime < 300)) {
                closeCard();
              }
            }, { passive: true });
          } else {
            // Re-attach close handler if button already exists
            const closeCard = () => {
              projectCard.classList.remove('active');
              projectCard.style.transform = '';
              document.body.style.overflow = '';
            };
            
            // Remove old listeners by cloning and replacing
            const newCloseBtn = closeBtn.cloneNode(true);
            closeBtn.parentNode.replaceChild(newCloseBtn, closeBtn);
            newCloseBtn.addEventListener('click', closeCard);
          }
        }
        
        await this.sceneManager.showProjectOnScreen(project);
      });
    });

    // Day/Night toggle
    const dayNightToggle = document.getElementById('day-night-toggle');
    if (dayNightToggle) {
      dayNightToggle.addEventListener('click', () => {
        this.sceneManager.toggleDayNightMode();
      });
    }

    // Mouse wheel navigation
    window.addEventListener('wheel', this.handleScroll.bind(this), { passive: false });
    
    // Touch gesture navigation for mobile
    this.setupTouchNavigation();
    
    // Keyboard navigation for accessibility
    this.setupKeyboardNavigation();
    
  }

  handleScroll(event) {
    const activeSection = document.querySelector('.content.active');

    if (!activeSection) return;

    const deltaY = this.normalizeWheelDelta(event);
    if (Math.abs(deltaY) < 2) return; // ignore tiny noise

    // If the wheel event happens over a nested scrollable element, prefer scrolling that element.
    const nestedScrollable = this.getClosestScrollableAncestor(event.target, activeSection);
    if (nestedScrollable && this.canScrollInDirection(nestedScrollable, deltaY)) {
      this.resetWheelIntent();
      return; // allow native scrolling in the nested element
    }

    // Otherwise, if the active section itself can scroll, let it scroll normally.
    if (this.canScrollInDirection(activeSection, deltaY)) {
      this.resetWheelIntent();
      return;
    }

    // We're at an edge (top or bottom). Only navigate sections after a stronger scroll "push".
    event.preventDefault();

    if (this.isScrolling) return;

    const direction = deltaY > 0 ? 1 : -1;
    if (!this.consumeWheelIntent(direction, Math.abs(deltaY))) {
      return;
    }

    const currentIndex = this.sections.indexOf(this.currentSection);
    const nextIndex = currentIndex + direction;
    
    if (nextIndex >= 0 && nextIndex < this.sections.length) {
      this.navigateToSection(this.sections[nextIndex]);
    }
  }

  normalizeWheelDelta(event) {
    // deltaMode: 0=pixel, 1=line, 2=page
    // Try to normalize to pixels so thresholds behave similarly across devices.
    if (event.deltaMode === 1) return event.deltaY * 16;
    if (event.deltaMode === 2) return event.deltaY * (window.innerHeight || 800);
    return event.deltaY;
  }

  getClosestScrollableAncestor(startNode, stopAtNode) {
    let node = startNode;
    while (node && node !== document && node !== stopAtNode) {
      if (node instanceof HTMLElement) {
        const style = window.getComputedStyle(node);
        const overflowY = style.overflowY;
        const canScroll =
          (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
          node.scrollHeight > node.clientHeight + 1;
        if (canScroll) return node;
      }
      node = node.parentNode;
    }
    return null;
  }

  canScrollInDirection(element, deltaY) {
    if (!element) return false;
    const canScroll = element.scrollHeight > element.clientHeight + 1;
    if (!canScroll) return false;

    const atTop = element.scrollTop <= 0;
    const atBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 1;
    const scrollingDown = deltaY > 0;
    return (scrollingDown && !atBottom) || (!scrollingDown && !atTop);
  }

  resetWheelIntent() {
    this._wheelIntent.accum = 0;
    this._wheelIntent.dir = 0;
    this._wheelIntent.lastTs = 0;
  }

  consumeWheelIntent(direction, absDelta) {
    const now = Date.now();
    const w = this._wheelIntent;

    const withinWindow = now - (w.lastTs || 0) <= this.wheelNavigateWindowMs;
    const sameDir = w.dir === direction;

    if (!withinWindow || !sameDir) {
      w.accum = 0;
    }

    w.dir = direction;
    w.lastTs = now;
    w.accum += absDelta;

    if (w.accum >= this.wheelNavigateThreshold) {
      this.resetWheelIntent();
      return true;
    }

    return false;
  }

  async navigateToSection(section) {
    if (this.isScrolling || section === this.currentSection) return;
    
    this.isScrolling = true;
    this.currentSection = section;

    // If we leave the work section, ensure the mobile project card is closed.
    if (section !== 'work') {
      const openCard = document.querySelector('.project-details-card.active');
      if (openCard) {
        openCard.classList.remove('active');
        openCard.style.transform = '';
        document.body.style.overflow = '';
      }
    }
    
    // Update navigation menu
    document.querySelectorAll('.nav-item').forEach(link => {
      link.classList.toggle('active', link.getAttribute('data-section') === section);
    });
    
    // Update visible section
    document.querySelectorAll('.content').forEach(s => {
      s.classList.toggle('active', s.classList.contains(section + '-content'));
    });
    
    // Transition camera
    await this.sceneManager.transitionToSection(section);
    
    // Reset scroll cooldown
    setTimeout(() => {
      this.isScrolling = false;
    }, this.scrollCooldown);
  }

  setupTouchNavigation() {
    let startY = 0;
    let startX = 0;
    let startTime = 0;
    let startedInProjectCard = false;
    const minSwipeDistance = 50;
    const maxSwipeTime = 500;
    const minSwipeVelocity = 0.6; // px/ms

    document.addEventListener('touchstart', (e) => {
      startY = e.touches[0].clientY;
      startX = e.touches[0].clientX;
      startTime = Date.now();
      startedInProjectCard = !!e.target.closest?.('.project-details-card.active');
    }, { passive: true });

    document.addEventListener('touchend', (e) => {
      if (this.isScrolling) return;

      // If swipe started inside the open project card, never change sections.
      // The card itself handles swipe-down to close.
      if (startedInProjectCard) {
        startedInProjectCard = false;
        return;
      }
      
      const endY = e.changedTouches[0].clientY;
      const endX = e.changedTouches[0].clientX;
      const endTime = Date.now();
      const deltaY = startY - endY;
      const deltaX = startX - endX;
      const deltaTime = endTime - startTime;

      // If the current section can scroll, don't treat normal scrolling as navigation.
      const activeSection = document.querySelector('.content.active');
      if (activeSection && activeSection.classList.contains('work-content')) {
        const workList = activeSection.querySelector('.work-list');
        const projectCard = activeSection.querySelector('.project-details-card');
        
        // Check if we're interacting with scrollable elements in work section
        if (workList && workList.scrollHeight > workList.clientHeight) {
          const atTop = workList.scrollTop <= 0;
          const atBottom = workList.scrollTop + workList.clientHeight >= workList.scrollHeight - 1;
          const swipingUp = deltaY > 0;
          if ((swipingUp && !atBottom) || (!swipingUp && !atTop)) {
            return;
          }
        }
        
        if (projectCard && projectCard.scrollHeight > projectCard.clientHeight) {
          const atTop = projectCard.scrollTop <= 0;
          const atBottom = projectCard.scrollTop + projectCard.clientHeight >= projectCard.scrollHeight - 1;
          const swipingUp = deltaY > 0;
          if ((swipingUp && !atBottom) || (!swipingUp && !atTop)) {
            return;
          }
        }
      }
      
      if (activeSection && activeSection.scrollHeight > activeSection.clientHeight + 1) {
        const atTop = activeSection.scrollTop <= 0;
        const atBottom = activeSection.scrollTop + activeSection.clientHeight >= activeSection.scrollHeight - 1;
        const swipingUp = deltaY > 0;
        if ((swipingUp && !atBottom) || (!swipingUp && !atTop)) {
          return;
        }
      }

      const velocity = Math.abs(deltaY) / Math.max(deltaTime, 1);
      const isVertical = Math.abs(deltaY) > Math.abs(deltaX);

      if (isVertical && Math.abs(deltaY) > minSwipeDistance && deltaTime < maxSwipeTime && velocity >= minSwipeVelocity) {
        const direction = deltaY > 0 ? 1 : -1;
        const currentIndex = this.sections.indexOf(this.currentSection);
        const nextIndex = currentIndex + direction;
        
        if (nextIndex >= 0 && nextIndex < this.sections.length) {
          this.navigateToSection(this.sections[nextIndex]);
        }
      }
    }, { passive: true });
  }

  setupKeyboardNavigation() {
    document.addEventListener('keydown', (e) => {
      if (this.isScrolling) return;
      
      const currentIndex = this.sections.indexOf(this.currentSection);
      let nextIndex = -1;

      switch (e.key) {
        case 'ArrowUp':
        case 'ArrowLeft':
          e.preventDefault();
          nextIndex = Math.max(0, currentIndex - 1);
          break;
        case 'ArrowDown':
        case 'ArrowRight':
          e.preventDefault();
          nextIndex = Math.min(this.sections.length - 1, currentIndex + 1);
          break;
        case 'Home':
          e.preventDefault();
          nextIndex = 0;
          break;
        case 'End':
          e.preventDefault();
          nextIndex = this.sections.length - 1;
          break;
        case 'Escape':
          // Close mobile menu if open
          const navigation = document.querySelector('.navigation');
          const mobileToggle = document.querySelector('.mobile-nav-toggle');
          if (navigation?.classList.contains('open')) {
            navigation.classList.remove('open');
            mobileToggle?.classList.remove('open');
            mobileToggle?.setAttribute('aria-expanded', 'false');
          document.body.style.overflow = 'hidden';
          }
          break;
      }

      if (nextIndex !== -1 && nextIndex !== currentIndex) {
        this.navigateToSection(this.sections[nextIndex]);
      }
    });
  }

  animate() {
    requestAnimationFrame(this.animate.bind(this));
    this.sceneManager.update();
  }
}

// Create custom cursor
function setupCustomCursor() {
  // Create cursor element
  const cursor = document.createElement('div');
  cursor.className = 'custom-cursor';
  document.body.appendChild(cursor);
  
  // Track mouse movements
  document.addEventListener('mousemove', (e) => {
    cursor.style.left = `${e.clientX}px`;
    cursor.style.top = `${e.clientY}px`;
  });
  
  // Change cursor on interactive elements
  const interactiveElements = document.querySelectorAll('.nav-item, .social-link, .work-item, button');
  
  interactiveElements.forEach(element => {
    element.addEventListener('mouseenter', () => {
      cursor.classList.add('cursor-hover');
    });
    
    element.addEventListener('mouseleave', () => {
      cursor.classList.remove('cursor-hover');
    });
  });
}

// Create loading screen
function setupLoadingScreen() {
  // Create loading screen elements
  const loadingScreen = document.createElement('div');
  loadingScreen.className = 'loading-screen';
  
  const loadingIcon = document.createElement('div');
  loadingIcon.className = 'loading-icon';
  
  const loadingCircle = document.createElement('div');
  loadingCircle.className = 'loading-circle';
  
  const loadingText = document.createElement('div');
  loadingText.className = 'loading-text';
  loadingText.textContent = 'LOADING';
  
  // Assemble loading screen
  loadingIcon.appendChild(loadingCircle);
  loadingIcon.appendChild(loadingText);
  loadingScreen.appendChild(loadingIcon);
  document.body.appendChild(loadingScreen);
  
  return loadingScreen;
}

// Setup required containers
function setupContainers() {
  // Create main container for the app if it doesn't exist
  let mainContainer = document.getElementById('main-container');
  if (!mainContainer) {
    mainContainer = document.createElement('div');
    mainContainer.id = 'main-container';
    document.body.appendChild(mainContainer);
  }

  // Create canvas container if it doesn't exist
  let canvasContainer = document.getElementById('canvas-container');
  if (!canvasContainer) {
    canvasContainer = document.createElement('div');
    canvasContainer.id = 'canvas-container';
    mainContainer.appendChild(canvasContainer);
  }

  // Create scene container if it doesn't exist
  let sceneContainer = document.getElementById('scene-container');
  if (!sceneContainer) {
    sceneContainer = document.createElement('div');
    sceneContainer.id = 'scene-container';
    canvasContainer.appendChild(sceneContainer);
  }
}

// Track assets loaded
const assetsToLoad = {
  models: 0,
  textures: 0
};

// Asset loading tracker
function incrementAssetLoaded(type) {
  assetsToLoad[type]++;
  updateLoadingProgress();
}

// Update loading progress
function updateLoadingProgress() {
  const total = Object.values(assetsToLoad).reduce((acc, val) => acc + val, 0);
  // Can be used to update loading bar if added later
}

// Main app initialization
window.addEventListener('DOMContentLoaded', () => {
  console.log('DOM content loaded, initializing app...');
  
  // Ensure our layout matches the real viewport height on mobile (Safari toolbars, etc).
  setAppHeightVar();
  window.addEventListener('resize', setAppHeightVar, { passive: true });
  window.addEventListener('orientationchange', setAppHeightVar, { passive: true });
  window.visualViewport?.addEventListener('resize', setAppHeightVar, { passive: true });
  window.visualViewport?.addEventListener('scroll', setAppHeightVar, { passive: true });

  // Setup required containers first
  setupContainers();
  
  // Project details card placement:
  // - Desktop: stays inside the work layout as a side panel.
  // - Mobile: becomes a singleton "bottom sheet" mounted under #main-container.
  const projectCard = document.querySelector('.project-details-card');
  const mainContainer = document.getElementById('main-container') || document.body;
  const originalMount = projectCard
    ? { parent: projectCard.parentElement, nextSibling: projectCard.nextSibling }
    : null;

  const syncProjectCardPlacement = () => {
    if (!projectCard) return;
    const isMobileLayout = window.innerWidth <= 768;

    // Always close when switching modes to prevent "stuck open" states.
    projectCard.classList.remove('active');
    projectCard.style.transform = '';
    document.body.style.overflow = '';

    if (isMobileLayout) {
      projectCard.classList.add('mobile-sheet');
      if (projectCard.parentElement !== mainContainer) {
        mainContainer.appendChild(projectCard);
      }
    } else {
      projectCard.classList.remove('mobile-sheet');
      if (originalMount?.parent) {
        // Move back to original place in the work layout.
        if (originalMount.nextSibling && originalMount.nextSibling.parentNode === originalMount.parent) {
          originalMount.parent.insertBefore(projectCard, originalMount.nextSibling);
        } else {
          originalMount.parent.appendChild(projectCard);
        }
      }
    }
  };

  syncProjectCardPlacement();
  window.addEventListener('resize', syncProjectCardPlacement, { passive: true });
  window.addEventListener('orientationchange', syncProjectCardPlacement, { passive: true });
  
  // Setup loading screen
  const loadingScreen = setupLoadingScreen();
  
  // Detect mobile device
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  // Initialize the app with loading callbacks
  try {
    // Initialize app
    const app = new Main();
    
    // Setup custom cursor only on non-mobile devices
    if (!isMobile) {
      setupCustomCursor();
    }
    
    // Add performance monitoring for mobile
    if (isMobile) {
      console.log('Mobile device detected - optimizations enabled');
      
      // Reduce animation frame rate on older mobile devices
      const isLowEndDevice = navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2;
      if (isLowEndDevice) {
        console.log('Low-end device detected - further optimizations applied');
      }
    }
    
    // Hide loading screen after everything is loaded
    const minLoadTime = isMobile ? 1000 : 1500; // Faster on mobile
    setTimeout(() => {
      loadingScreen.classList.add('hidden');
      
      // Remove loading screen after transition
      setTimeout(() => {
        if (loadingScreen.parentNode) {
          loadingScreen.remove();
        }
      }, 500);
      
      // Announce content loaded for screen readers
      const announcement = document.createElement('div');
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.className = 'sr-only';
      announcement.textContent = 'Portfolio content loaded successfully';
      document.body.appendChild(announcement);
      
      setTimeout(() => {
        if (announcement.parentNode) {
          announcement.remove();
        }
      }, 3000);
      
    }, minLoadTime);
    
    // Add app instance to window for debugging
    window.app = app;
    
    console.log('App initialized successfully');
  } catch (error) {
    console.error('Error initializing app:', error);
    
    // Hide loading screen on error
    loadingScreen.classList.add('hidden');
    
    // Display user-friendly error message
    const errorContainer = document.createElement('div');
    errorContainer.className = 'error-container';
    errorContainer.innerHTML = `
      <div class="error-message">
        <h2>Oops! Something went wrong</h2>
        <p>The portfolio site encountered an issue while loading. Please try refreshing the page.</p>
        <button onclick="window.location.reload()" class="retry-button">Retry</button>
        <details>
          <summary>Technical details</summary>
          <p>${error.message}</p>
        </details>
      </div>
    `;
    document.body.appendChild(errorContainer);
  }
}); 